/**
 * Product Model
 * Manages unit prices and configs for AuraPharma medicines.
 */

import { 
  collection, 
  doc, 
  getDocs, 
  setDoc,
  updateDoc 
} from 'firebase/firestore';
import { db } from '../firebase.js';

const PRODUCTS_COLL = 'products';

const DEFAULT_PRODUCTS = [
  { id: 'bonuramax', name: 'Bonuramax', price: 49.00 },
  { id: 'etoura-mr', name: 'Etoura-MR', price: 35.00 },
  { id: 'bonurafrac', name: 'BonuraFrac', price: 29.00 },
  { id: 'tenjofit', name: 'Tenjofit', price: 55.00 },
  { id: 'uracid-d', name: 'Uracid D', price: 18.00 }
];

/**
 * Retrieve all products from Firestore database
 * @returns {Promise<Array>}
 */
export async function getProducts() {
  try {
    const collRef = collection(db, PRODUCTS_COLL);
    
    // Create a 3-second timeout promise to prevent infinite loading when offline
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error("Firestore fetch timeout")), 3000)
    );
    
    // Race getDocs query against the timeout
    const snapshot = await Promise.race([
      getDocs(collRef),
      timeoutPromise
    ]);
    
    if (snapshot.empty) {
      // Auto-seeding
      try {
        await seedDefaultProducts();
        const freshSnapshot = await getDocs(collRef);
        const docs = [];
        freshSnapshot.forEach(doc => docs.push({ id: doc.id, ...doc.data() }));
        return docs;
      } catch (seedErr) {
        console.warn("Seeding products failed, falling back to local defaults:", seedErr);
        return getLocalFallbackProducts();
      }
    }
    
    const docs = [];
    snapshot.forEach(doc => docs.push({ id: doc.id, ...doc.data() }));
    return docs;
  } catch (err) {
    console.warn("Firestore products fetch failed or timed out. Falling back to local cache/defaults. Error:", err);
    return getLocalFallbackProducts();
  }
}

/**
 * Helper to fetch local storage custom prices merged with defaults
 */
function getLocalFallbackProducts() {
  try {
    const stored = localStorage.getItem('pharma-custom-prices');
    const customPrices = stored ? JSON.parse(stored) : null;
    return DEFAULT_PRODUCTS.map(p => {
      if (customPrices && customPrices[p.id] !== undefined) {
        return { ...p, price: parseFloat(customPrices[p.id]) };
      }
      return p;
    });
  } catch (e) {
    console.warn("Error reading local backup prices:", e);
    return DEFAULT_PRODUCTS;
  }
}

/**
 * Update the unit price for a specific product
 * @param {string} productId 
 * @param {number} price 
 * @returns {Promise<void>}
 */
export async function updateProductPrice(productId, price) {
  // Update local storage backup first to preserve custom price offline
  try {
    const stored = localStorage.getItem('pharma-custom-prices');
    const prices = stored ? JSON.parse(stored) : {};
    prices[productId] = parseFloat(price);
    localStorage.setItem('pharma-custom-prices', JSON.stringify(prices));
  } catch (e) {
    console.warn("Could not save price backup to localStorage:", e);
  }

  const docRef = doc(db, PRODUCTS_COLL, productId);
  return await updateDoc(docRef, { price: parseFloat(price) });
}

/**
 * Seed initial default products with default unit prices
 */
export async function seedDefaultProducts() {
  const collRef = collection(db, PRODUCTS_COLL);
  for (const prod of DEFAULT_PRODUCTS) {
    const docRef = doc(collRef, prod.id);
    await setDoc(docRef, {
      name: prod.name,
      price: prod.price
    });
  }
  console.log("Default products seeded successfully.");
}
